import { useCallback, useEffect, useState, useRef } from 'react';
import type { ChangeEvent, DragEvent, MouseEvent as ReactMouseEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faPlus, faSearch, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Input from '../../form/Input';
import BigButton from '../../button/BigButton';
import FileUpload from '../../form/FileUpload';
import apiClient from '../../../service/apiClient';
import {
  usePurchaseLandingTrackingStore,
  IPurchaseLandingTrackingCode,
  IPurchaseLandingTrackingCodeCreateRequest,
  IPackageInput,
  IProductInfo,
} from '../../../store/usePurchaseLandingTrackingStore';
import { useFranchiseStore } from '../../../store/useFranchise';
import { getFranchiseMailOrderNumber } from '../../../util/franchise';
import { preventWheelChange } from '../../../util/numberInput';
import LandingBusinessFooter from '../common/LandingBusinessFooter';
import '../fortuneLab/CodeModal.css';

// 상품 인터페이스
interface IProduct {
  id: string;
  productName: string;
  custInfoCount: 1 | 2;
  price: number;
  discountPrice: number;
}

interface CodeModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  code?: IPurchaseLandingTrackingCode | null;
  onClose: () => void;
  onSuccess?: () => Promise<void> | void;
}

// 패키지 + 상품정보를 함께 관리
interface IPackageWithProducts extends IPackageInput {
  personType?: 1 | 2;
  products?: IProduct[]; // UI 표시용 상품 정보
}

interface FormState {
  campaignName: string;
  personType: 1 | 2;
  showFooter: boolean;
  packages: IPackageWithProducts[];
  topImageUrls: string[];
  bottomImageUrls: string[];
  facebookPixelId: string;
  kakaoPixelId: string;
  googleAdsPixelId: string;
  googleAdsEventSnippet: string;
  daangnPixelId: string;
  tiktokPixelId: string;
  bankName: string;
  bankAccount: string;
  bankAccountHolder: string;
  purchaseButtonName: string;
  documentTitle: string;
}

const extractFileName = (assetUrl: string) => {
  try {
    const decoded = decodeURIComponent(assetUrl);
    const lastSegment = decoded.split('?')[0]?.split('/').pop();
    return lastSegment && lastSegment.trim().length > 0 ? lastSegment : decoded;
  } catch (_) {
    const fallback = assetUrl.split('?')[0];
    const lastSegment = fallback.split('/').pop();
    return lastSegment && lastSegment.trim().length > 0 ? lastSegment : assetUrl;
  }
};

const getProductPersonType = (product?: Pick<IProduct, 'custInfoCount'> | IProductInfo): 1 | 2 =>
  product?.custInfoCount === 2 ? 2 : 1;

const getPackagePersonType = (
  pkg: { personType?: 1 | 2; products?: Array<Pick<IProduct, 'custInfoCount'> | IProductInfo> } | undefined,
  fallback: 1 | 2
): 1 | 2 => {
  if (pkg?.personType === 1 || pkg?.personType === 2) return pkg.personType;
  if (pkg?.products && pkg.products.length > 0) {
    return pkg.products.some(product => getProductPersonType(product) === 2) ? 2 : 1;
  }
  return fallback;
};

const buildInitialForm = (code: IPurchaseLandingTrackingCode | null | undefined): FormState => {
  return {
    campaignName: code?.campaignName ?? '',
    personType: (code?.personType as 1 | 2) ?? 1,
    showFooter: code?.showFooter ?? true,
    packages: code?.packages?.map(p => ({
      id: p.id,
      packageName: p.packageName,
      packagePrice: p.packagePrice,
      // 백엔드 products(id, name)를 프론트엔드 형식으로 변환
      productIds: p.products?.map(prod => prod.id),
      personType: getPackagePersonType(p, formPersonTypeFromCode(code)),
      products: p.products?.map(prod => ({
        id: prod.id,
        productName: prod.name,
        custInfoCount: getProductPersonType(prod),
        price: 0,
        discountPrice: 0,
      })),
    })) ?? [],
    topImageUrls: code?.topImageUrls ?? [],
    bottomImageUrls: code?.bottomImageUrls ?? [],
    facebookPixelId: code?.facebookPixelId ?? '',
    kakaoPixelId: code?.kakaoPixelId ?? '',
    googleAdsPixelId: code?.googleAdsPixelId ?? '',
    googleAdsEventSnippet: code?.googleAdsEventSnippet ?? '',
    daangnPixelId: code?.daangnPixelId ?? '',
    tiktokPixelId: code?.tiktokPixelId ?? '',
    bankName: code?.bankName ?? '',
    bankAccount: code?.bankAccount ?? '',
    bankAccountHolder: code?.bankAccountHolder ?? '',
    purchaseButtonName: code?.purchaseButtonName ?? '',
    documentTitle: code?.documentTitle ?? '',
  };
};

const formPersonTypeFromCode = (code: IPurchaseLandingTrackingCode | null | undefined): 1 | 2 =>
  code?.personType === 2 ? 2 : 1;

const hasAnyPixel = (code: IPurchaseLandingTrackingCode | null | undefined): boolean => {
  if (!code) return false;
  return !!(
    code.facebookPixelId ||
    code.kakaoPixelId ||
    code.googleAdsPixelId ||
    code.googleAdsEventSnippet ||
    code.daangnPixelId ||
    code.tiktokPixelId
  );
};

const PurchaseLandingTrackingCodeModal = ({
  isOpen,
  mode,
  code,
  onClose,
  onSuccess,
}: CodeModalProps) => {
  const { createCode, updateCode } = usePurchaseLandingTrackingStore();
  const myfranchise = useFranchiseStore(state => state.myfranchise);
  const getMyFranchise = useFranchiseStore(state => state.getMyFranchise);
  const [form, setForm] = useState<FormState>(() => buildInitialForm(code));
  const [pendingTopFiles, setPendingTopFiles] = useState<File[]>([]);
  const [pendingBottomFiles, setPendingBottomFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragTarget, setDragTarget] = useState<'top' | 'bottom' | null>(null);
  const [showPixelSettings, setShowPixelSettings] = useState(() => hasAnyPixel(code));
  const [newPackageName, setNewPackageName] = useState('');
  const [newPackagePrice, setNewPackagePrice] = useState('');
  const [newPackagePersonType, setNewPackagePersonType] = useState<1 | 2>(1);

  // 상품 검색 관련 상태
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productSearchResults, setProductSearchResults] = useState<IProduct[]>([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProductsForNewPackage, setSelectedProductsForNewPackage] = useState<IProduct[]>([]);
  const [expandedPackageIndex, setExpandedPackageIndex] = useState<number | null>(null);
  const [editingPackageProductSearch, setEditingPackageProductSearch] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    getMyFranchise();
  }, [getMyFranchise, isOpen]);

  // 상품 검색 API
  const searchProducts = useCallback(async (query: string, personType: 1 | 2) => {
    if (!query.trim()) {
      setProductSearchResults([]);
      return;
    }
    setProductSearchLoading(true);
    try {
      const response = await apiClient.get('/product', {
        params: {
          productName: query,
          displayYn: 'Y',
          confirmYn: 'Y',
          custInfoCount: personType,
          size: 20,
        },
      });
      setProductSearchResults(response.data?.content ?? []);
    } catch (error) {
      console.error('상품 검색 실패', error);
      setProductSearchResults([]);
    } finally {
      setProductSearchLoading(false);
    }
  }, []);

  // 디바운스 검색
  const handleProductSearchChange = useCallback((query: string, forNewPackage: boolean, personType: 1 | 2) => {
    if (forNewPackage) {
      setProductSearchQuery(query);
    } else {
      setEditingPackageProductSearch(query);
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      searchProducts(query, personType);
    }, 300);
  }, [searchProducts]);

  // 새 패키지용 상품 선택/해제
  const toggleProductForNewPackage = useCallback((product: IProduct) => {
    setSelectedProductsForNewPackage(prev => {
      const exists = prev.some(p => p.id === product.id);
      if (exists) {
        return prev.filter(p => p.id !== product.id);
      }
      return [...prev, product];
    });
  }, []);

  // 기존 패키지의 상품 추가 (상품 정보도 함께 저장)
  const addProductToPackage = useCallback((packageIndex: number, product: IProduct) => {
    setForm(prev => {
      const updated = [...prev.packages];
      const pkg = updated[packageIndex];
      const currentProductIds = pkg.productIds ?? [];
      const currentProducts = pkg.products ?? [];
      if (!currentProductIds.includes(product.id)) {
        updated[packageIndex] = {
          ...pkg,
          productIds: [...currentProductIds, product.id],
          products: [...currentProducts, product],
        };
      }
      return { ...prev, packages: updated };
    });
  }, []);

  // 기존 패키지의 상품 제거
  const removeProductFromPackage = useCallback((packageIndex: number, productId: string) => {
    setForm(prev => {
      const updated = [...prev.packages];
      const pkg = updated[packageIndex];
      updated[packageIndex] = {
        ...pkg,
        productIds: (pkg.productIds ?? []).filter(id => id !== productId),
        products: (pkg.products ?? []).filter(p => p.id !== productId),
      };
      return { ...prev, packages: updated };
    });
  }, []);

  const resetState = useCallback(() => {
    setForm(buildInitialForm(code));
    setPendingTopFiles([]);
    setPendingBottomFiles([]);
    setUploading(false);
    setSaving(false);
    setDragIndex(null);
    setDragTarget(null);
    setShowPixelSettings(hasAnyPixel(code));
    setNewPackageName('');
    setNewPackagePrice('');
    setNewPackagePersonType(1);
    // 상품 검색 상태 초기화
    setProductSearchQuery('');
    setProductSearchResults([]);
    setProductSearchLoading(false);
    setSelectedProductsForNewPackage([]);
    setExpandedPackageIndex(null);
    setEditingPackageProductSearch('');
  }, [code]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    resetState();
  }, [code, isOpen, resetState]);

  // 상단 이미지 업로드
  useEffect(() => {
    if (pendingTopFiles.length === 0) return;
    let isCancelled = false;

    const uploadAssets = async () => {
      setUploading(true);
      const uploadedUrls: string[] = [];
      try {
        for (const file of pendingTopFiles) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await apiClient.post('/upload-file/upload', formData);
          const fileUrl = response.data?.fileFullUrl ?? '';
          if (fileUrl) uploadedUrls.push(fileUrl);
        }
        if (!isCancelled && uploadedUrls.length > 0) {
          setForm(prev => ({
            ...prev,
            topImageUrls: [...prev.topImageUrls, ...uploadedUrls],
          }));
        }
      } catch (error) {
        console.error('상단 이미지 업로드 실패', error);
        if (!isCancelled) toast.error('상단 이미지 업로드에 실패했습니다.');
      } finally {
        if (!isCancelled) {
          setUploading(false);
          setPendingTopFiles([]);
        }
      }
    };
    uploadAssets();
    return () => {
      isCancelled = true;
    };
  }, [pendingTopFiles]);

  // 하단 이미지 업로드
  useEffect(() => {
    if (pendingBottomFiles.length === 0) return;
    let isCancelled = false;

    const uploadAssets = async () => {
      setUploading(true);
      const uploadedUrls: string[] = [];
      try {
        for (const file of pendingBottomFiles) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await apiClient.post('/upload-file/upload', formData);
          const fileUrl = response.data?.fileFullUrl ?? '';
          if (fileUrl) uploadedUrls.push(fileUrl);
        }
        if (!isCancelled && uploadedUrls.length > 0) {
          setForm(prev => ({
            ...prev,
            bottomImageUrls: [...prev.bottomImageUrls, ...uploadedUrls],
          }));
        }
      } catch (error) {
        console.error('하단 이미지 업로드 실패', error);
        if (!isCancelled) toast.error('하단 이미지 업로드에 실패했습니다.');
      } finally {
        if (!isCancelled) {
          setUploading(false);
          setPendingBottomFiles([]);
        }
      }
    };
    uploadAssets();
    return () => {
      isCancelled = true;
    };
  }, [pendingBottomFiles]);

  const handleInputChange =
    (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setForm(prev => ({ ...prev, [field]: value }));
    };

  const handleAddPackage = useCallback(() => {
    const name = newPackageName.trim();
    const price = parseInt(newPackagePrice, 10);

    if (!name) {
      toast.error('패키지명을 입력해주세요.');
      return;
    }
    if (isNaN(price) || price <= 0) {
      toast.error('유효한 가격을 입력해주세요.');
      return;
    }
    if (selectedProductsForNewPackage.length === 0) {
      toast.error('패키지에 상품을 1개 이상 추가해주세요.');
      return;
    }

    // 새 패키지에 선택된 상품 정보도 함께 저장
    const newPackage: IPackageWithProducts = {
      packageName: name,
      packagePrice: price,
      personType: newPackagePersonType,
      productIds: selectedProductsForNewPackage.map(p => p.id),
      products: selectedProductsForNewPackage,
    };

    setForm(prev => ({
      ...prev,
      packages: [...prev.packages, newPackage],
    }));
    setNewPackageName('');
    setNewPackagePrice('');
    setNewPackagePersonType(1);
    setSelectedProductsForNewPackage([]);
    setProductSearchQuery('');
    setProductSearchResults([]);
  }, [newPackageName, newPackagePersonType, newPackagePrice, selectedProductsForNewPackage]);

  const handleRemovePackage = useCallback((index: number) => {
    setForm(prev => ({
      ...prev,
      packages: prev.packages.filter((_, i) => i !== index),
    }));
  }, []);

  const handleUpdatePackage = useCallback(
    (index: number, field: 'packageName' | 'packagePrice', value: string) => {
      setForm(prev => {
        const updated = [...prev.packages];
        if (field === 'packageName') {
          updated[index] = { ...updated[index], packageName: value };
        } else {
          const price = parseInt(value, 10);
          updated[index] = { ...updated[index], packagePrice: isNaN(price) ? 0 : price };
        }
        return { ...prev, packages: updated };
      });
    },
    []
  );

  const handleRemoveAsset = useCallback(
    (target: 'top' | 'bottom', index: number) =>
      (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const field = target === 'top' ? 'topImageUrls' : 'bottomImageUrls';
        setForm(prev => ({
          ...prev,
          [field]: prev[field].filter((_, assetIndex) => assetIndex !== index),
        }));
      },
    []
  );

  const handleDragStart = useCallback(
    (target: 'top' | 'bottom', index: number) => (event: DragEvent<HTMLDivElement>) => {
      setDragIndex(index);
      setDragTarget(target);
      event.dataTransfer.setData('text/plain', String(index));
      event.dataTransfer.effectAllowed = 'move';
    },
    []
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback(
    (target: 'top' | 'bottom', index: number) => (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (dragIndex === null || dragIndex === index || dragTarget !== target) {
        setDragIndex(null);
        setDragTarget(null);
        return;
      }
      const field = target === 'top' ? 'topImageUrls' : 'bottomImageUrls';
      setForm(prev => {
        const nextAssets = [...prev[field]];
        const [moved] = nextAssets.splice(dragIndex, 1);
        nextAssets.splice(index, 0, moved);
        return { ...prev, [field]: nextAssets };
      });
      setDragIndex(null);
      setDragTarget(null);
    },
    [dragIndex, dragTarget]
  );

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragTarget(null);
  }, []);

  const handleSubmit = useCallback(
    async (event?: ReactMouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      if (saving || uploading) return;

      if (!form.campaignName.trim()) {
        toast.error('캠페인명을 입력해주세요.');
        return;
      }
      if (form.packages.length === 0) {
        toast.error('패키지를 최소 1개 이상 추가해주세요.');
        return;
      }

      // 패키지 유효성 검증
      for (const pkg of form.packages) {
        if (!pkg.packageName.trim()) {
          toast.error('패키지명이 비어있는 항목이 있습니다.');
          return;
        }
        if (pkg.packagePrice <= 0) {
          toast.error('패키지 가격은 0보다 커야 합니다.');
          return;
        }
        if (!pkg.productIds || pkg.productIds.length === 0) {
          toast.error(`"${pkg.packageName}" 패키지에 상품을 1개 이상 추가해주세요.`);
          return;
        }
      }

      // 계좌 필수 검증
      if (!form.bankName.trim() || !form.bankAccount.trim() || !form.bankAccountHolder.trim()) {
        toast.error('입금 계좌 정보를 모두 입력해주세요.');
        return;
      }

      const topImageUrls = form.topImageUrls.map(a => a.trim()).filter(Boolean);
      const bottomImageUrls = form.bottomImageUrls.map(a => a.trim()).filter(Boolean);

      const payload: IPurchaseLandingTrackingCodeCreateRequest = {
        campaignName: form.campaignName.trim(),
        showFooter: form.showFooter,
        packages: form.packages.map(({ products, ...pkg }) => pkg),
        topImageUrls: topImageUrls.length > 0 ? topImageUrls : undefined,
        bottomImageUrls: bottomImageUrls.length > 0 ? bottomImageUrls : undefined,
        facebookPixelId: form.facebookPixelId.trim() || undefined,
        kakaoPixelId: form.kakaoPixelId.trim() || undefined,
        googleAdsPixelId: form.googleAdsPixelId.trim() || undefined,
        googleAdsEventSnippet: form.googleAdsEventSnippet.trim() || undefined,
        daangnPixelId: form.daangnPixelId.trim() || undefined,
        tiktokPixelId: form.tiktokPixelId.trim() || undefined,
        bankName: form.bankName.trim() || undefined,
        bankAccount: form.bankAccount.trim() || undefined,
        bankAccountHolder: form.bankAccountHolder.trim() || undefined,
        purchaseButtonName: form.purchaseButtonName.trim() || undefined,
        documentTitle: form.documentTitle.trim() || undefined,
      };

      setSaving(true);
      try {
        if (mode === 'create') {
          await createCode(payload);
          handleClose();
        } else if (code) {
          const success = await updateCode(code.id, payload);
          if (success) {
            if (onSuccess) await onSuccess();
            handleClose();
          }
        }
      } finally {
        setSaving(false);
      }
    },
    [code, createCode, updateCode, form, handleClose, mode, onSuccess, saving, uploading]
  );

  const hasTopImages = form.topImageUrls.length > 0;
  const hasBottomImages = form.bottomImageUrls.length > 0;
  const previewPersonType = form.packages.length > 0
    ? (form.packages.some(pkg => getPackagePersonType(pkg, form.personType) === 2) ? 2 : 1)
    : newPackagePersonType;

  if (!isOpen) return null;

  const heading = mode === 'create' ? '구매 랜딩 캠페인 생성' : '구매 랜딩 캠페인 수정';
  const submitLabel =
    mode === 'create' ? (saving ? '생성 중...' : '생성') : saving ? '수정 중...' : '수정';

  const renderAssetList = (assets: string[], target: 'top' | 'bottom') => (
    <div className="landing-assets-list">
      {assets.map((asset, index) => (
        <div
          key={`${asset}-${index}`}
          className="landing-asset-card"
          draggable
          onDragStart={handleDragStart(target, index)}
          onDragOver={handleDragOver}
          onDrop={handleDrop(target, index)}
          onDragEnd={handleDragEnd}
        >
          <button
            type="button"
            className="landing-asset-drag"
            aria-label="순서 변경"
            disabled={uploading || saving}
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </button>
          <div className="landing-asset-info">
            <span className="landing-asset-index">#{index + 1}</span>
            <span className="landing-asset-name">{extractFileName(asset)}</span>
          </div>
          <button
            type="button"
            className="landing-asset-remove"
            onClick={handleRemoveAsset(target, index)}
            disabled={saving || uploading}
          >
            <FontAwesomeIcon icon={faTrash} />
            제거
          </button>
        </div>
      ))}
    </div>
  );

  const fieldTitleStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#333',
    marginBottom: '6px',
    textAlign: 'left',
  };

  const selectSmallStyle: React.CSSProperties = {
    height: '44px',
    border: '1px solid #dddddd',
    borderRadius: '8px',
    padding: '0 24px 0 8px',
    fontSize: '14px',
    outline: 'none',
    backgroundColor: '#fff',
    appearance: 'none',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    boxSizing: 'border-box',
    flex: 1,
    minWidth: 0,
  };

  const genderButtonStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    height: '52px',
    border: isActive ? '2px solid #4F46E5' : '1px solid #dddddd',
    borderRadius: '8px',
    backgroundColor: isActive ? '#EEF2FF' : '#fff',
    color: isActive ? '#4F46E5' : '#333',
    fontSize: '16px',
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer',
  });

  const genderButtonStyleSmall = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    height: '44px',
    border: isActive ? '2px solid #4F46E5' : '1px solid #dddddd',
    borderRadius: '8px',
    backgroundColor: isActive ? '#EEF2FF' : '#fff',
    color: isActive ? '#4F46E5' : '#333',
    fontSize: '14px',
    fontWeight: isActive ? 600 : 400,
    cursor: 'pointer',
  });

  const radioLabelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    fontSize: '12px',
    color: '#666',
    cursor: 'pointer',
  };

  const radioInputStyle: React.CSSProperties = {
    margin: 0,
    width: '12px',
    height: '12px',
  };

  const render1PersonPreview = () => (
    <form className="fortune_lab_form landing-preview__form" onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* 이름 */}
      <div style={{ width: '84%' }}>
        <div style={fieldTitleStyle}>이름</div>
        <input type="text" placeholder="이름을 입력하세요" disabled style={{ width: '100%' }} />
      </div>
      {/* 연락처 */}
      <div style={{ width: '84%' }}>
        <div style={fieldTitleStyle}>연락처</div>
        <input type="tel" placeholder="01012345678" disabled style={{ width: '100%' }} />
      </div>
      {/* 성별 */}
      <div style={{ width: '84%' }}>
        <div style={fieldTitleStyle}>성별</div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" style={genderButtonStyle(true)} disabled>남성</button>
          <button type="button" style={genderButtonStyle(false)} disabled>여성</button>
        </div>
      </div>
      {/* 생년월일 */}
      <div style={{ width: '84%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <div style={fieldTitleStyle}>생년월일</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <label style={radioLabelStyle}>
              <input type="radio" name="solarType" checked readOnly style={radioInputStyle} />
              양력
            </label>
            <label style={radioLabelStyle}>
              <input type="radio" name="solarType" readOnly style={radioInputStyle} />
              음력
            </label>
            <label style={radioLabelStyle}>
              <input type="radio" name="solarType" readOnly style={radioInputStyle} />
              윤달
            </label>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <select disabled style={selectSmallStyle}><option value="">년</option></select>
          <select disabled style={selectSmallStyle}><option value="">월</option></select>
          <select disabled style={selectSmallStyle}><option value="">일</option></select>
        </div>
      </div>
      {/* 태어난 시간 */}
      <div style={{ width: '84%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
          <div style={fieldTitleStyle}>태어난 시간</div>
          <label style={radioLabelStyle}>
            <input type="checkbox" style={radioInputStyle} />
            시간모름
          </label>
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <select disabled style={selectSmallStyle}><option value="">시</option></select>
          <select disabled style={selectSmallStyle}><option value="">분</option></select>
        </div>
      </div>
      {form.packages.length > 0 && (
        <div style={{ width: '84%', marginTop: '12px', textAlign: 'left' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>패키지 선택</div>
          {form.packages.map((pkg, index) => (
            <label
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px',
                cursor: 'pointer',
              }}
            >
              <input type="radio" name="package" disabled style={{ margin: 0, flexShrink: 0, width: '16px', height: '16px' }} />
              <span style={{ fontSize: '14px' }}>
                {pkg.packageName} - {pkg.packagePrice.toLocaleString()}원
              </span>
            </label>
          ))}
        </div>
      )}
    </form>
  );

  const render2PersonPreview = () => (
    <form className="fortune_lab_form landing-preview__form" onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* 신청자 연락처 */}
      <div style={{ width: '84%' }}>
        <div style={fieldTitleStyle}>신청자 연락처</div>
        <input type="tel" placeholder="01012345678" disabled style={{ width: '100%' }} />
      </div>
      {/* 신청자 */}
      <div
        style={{
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '8px',
          width: '84%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontWeight: '600', marginBottom: '12px', color: '#4F46E5' }}>신청자</div>
        {/* 이름 */}
        <div style={{ marginBottom: '8px' }}>
          <div style={fieldTitleStyle}>이름</div>
          <input type="text" placeholder="이름을 입력하세요" disabled style={{ width: '100%' }} />
        </div>
        {/* 성별 */}
        <div style={{ marginBottom: '8px' }}>
          <div style={fieldTitleStyle}>성별</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" style={genderButtonStyleSmall(true)} disabled>남성</button>
            <button type="button" style={genderButtonStyleSmall(false)} disabled>여성</button>
          </div>
        </div>
        {/* 생년월일 */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={fieldTitleStyle}>생년월일</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <label style={radioLabelStyle}>
                <input type="radio" name="solarType0" checked readOnly style={radioInputStyle} />
                양력
              </label>
              <label style={radioLabelStyle}>
                <input type="radio" name="solarType0" readOnly style={radioInputStyle} />
                음력
              </label>
              <label style={radioLabelStyle}>
                <input type="radio" name="solarType0" readOnly style={radioInputStyle} />
                윤달
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <select disabled style={selectSmallStyle}><option value="">년</option></select>
            <select disabled style={selectSmallStyle}><option value="">월</option></select>
            <select disabled style={selectSmallStyle}><option value="">일</option></select>
          </div>
        </div>
        {/* 태어난 시간 */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={fieldTitleStyle}>태어난 시간</div>
            <label style={radioLabelStyle}>
              <input type="checkbox" style={radioInputStyle} />
              시간모름
            </label>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <select disabled style={selectSmallStyle}><option value="">시</option></select>
            <select disabled style={selectSmallStyle}><option value="">분</option></select>
          </div>
        </div>
      </div>
      {/* 상대방 */}
      <div
        style={{
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          marginBottom: '8px',
          width: '84%',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontWeight: '600', marginBottom: '12px', color: '#7C3AED' }}>상대방</div>
        {/* 이름 */}
        <div style={{ marginBottom: '8px' }}>
          <div style={fieldTitleStyle}>이름</div>
          <input type="text" placeholder="이름을 입력하세요" disabled style={{ width: '100%' }} />
        </div>
        {/* 성별 */}
        <div style={{ marginBottom: '8px' }}>
          <div style={fieldTitleStyle}>성별</div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" style={genderButtonStyleSmall(true)} disabled>남성</button>
            <button type="button" style={genderButtonStyleSmall(false)} disabled>여성</button>
          </div>
        </div>
        {/* 생년월일 */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={fieldTitleStyle}>생년월일</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <label style={radioLabelStyle}>
                <input type="radio" name="solarType1" checked readOnly style={radioInputStyle} />
                양력
              </label>
              <label style={radioLabelStyle}>
                <input type="radio" name="solarType1" readOnly style={radioInputStyle} />
                음력
              </label>
              <label style={radioLabelStyle}>
                <input type="radio" name="solarType1" readOnly style={radioInputStyle} />
                윤달
              </label>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <select disabled style={selectSmallStyle}><option value="">년</option></select>
            <select disabled style={selectSmallStyle}><option value="">월</option></select>
            <select disabled style={selectSmallStyle}><option value="">일</option></select>
          </div>
        </div>
        {/* 태어난 시간 */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <div style={fieldTitleStyle}>태어난 시간</div>
            <label style={radioLabelStyle}>
              <input type="checkbox" style={radioInputStyle} />
              시간모름
            </label>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <select disabled style={selectSmallStyle}><option value="">시</option></select>
            <select disabled style={selectSmallStyle}><option value="">분</option></select>
          </div>
        </div>
      </div>
      {form.packages.length > 0 && (
        <div style={{ width: '84%', marginTop: '12px', textAlign: 'left' }}>
          <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px' }}>패키지 선택</div>
          {form.packages.map((pkg, index) => (
            <label
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '8px',
                cursor: 'pointer',
              }}
            >
              <input type="radio" name="package2" disabled style={{ margin: 0, flexShrink: 0, width: '16px', height: '16px' }} />
              <span style={{ fontSize: '14px' }}>
                {pkg.packageName} - {pkg.packagePrice.toLocaleString()}원
              </span>
            </label>
          ))}
        </div>
      )}
    </form>
  );

  return (
    <div className="fortunelab_modal_overlay">
      <div
        className="fortunelab_modal fortunelab_modal--wide"
        onClick={event => event.stopPropagation()}
      >
        <div className="fortunelab_modal_header">
          <h2>{heading}</h2>
          <button
            className="fortunelab_modal_close"
            onClick={handleClose}
            style={{ fontSize: '35px', padding: '8px 12px' }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="fortunelab_modal_body fortunelab_modal_body--grid">
          <div className="form-section">
            <div className="form-field">
              <label style={{ fontSize: '16px', fontWeight: '600' }}>
                캠페인명 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <Input
                value={form.campaignName}
                onChange={handleInputChange('campaignName')}
                placeholder="캠페인명을 입력하세요"
                width="100%"
                textAlign="left"
                disabled={saving}
              />
            </div>

            <div className="form-field">
              <label style={{ fontSize: '16px', fontWeight: '600' }}>
                패키지 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <p className="form-field-desc">고객이 선택할 수 있는 패키지를 추가해주세요.</p>

              {/* 등록된 패키지 목록 */}
              {form.packages.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px', marginBottom: '12px' }}>
                  {form.packages.map((pkg, index) => (
                    <div
                      key={index}
                      style={{
                        border: expandedPackageIndex === index ? '2px solid #4F46E5' : '1px solid #ddd',
                        borderRadius: '8px',
                        backgroundColor: '#f9fafb',
                        overflow: 'hidden',
                      }}
                    >
                      {/* 패키지 기본 정보 */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <input
                          type="text"
                          value={pkg.packageName}
                          onChange={e => handleUpdatePackage(index, 'packageName', e.target.value)}
                          placeholder="패키지명"
                          disabled={saving}
                          style={{
                            flex: '1 1 140px',
                            minWidth: '120px',
                            padding: '6px 10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                          }}
                        />
                        <input
                          type="number"
                          value={pkg.packagePrice || ''}
                          onChange={e => handleUpdatePackage(index, 'packagePrice', e.target.value)}
                          placeholder="가격"
                          disabled={saving}
                          onWheel={preventWheelChange}
                          style={{
                            flex: '0 1 112px',
                            minWidth: '96px',
                            padding: '6px 10px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '14px',
                          }}
                        />
                        <span style={{ fontSize: '14px', color: '#6b7280', flexShrink: 0 }}>원</span>
                        <span
                          style={{
                            width: '58px',
                            padding: '4px 8px',
                            borderRadius: '999px',
                            backgroundColor: getPackagePersonType(pkg, form.personType) === 1 ? '#dbeafe' : '#ede9fe',
                            color: getPackagePersonType(pkg, form.personType) === 1 ? '#1e40af' : '#6d28d9',
                            fontSize: '14px',
                            fontWeight: 600,
                            textAlign: 'center',
                            flexShrink: 0,
                            boxSizing: 'border-box',
                          }}
                        >
                          {getPackagePersonType(pkg, form.personType)}인
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setExpandedPackageIndex(expandedPackageIndex === index ? null : index);
                            setEditingPackageProductSearch('');
                            setProductSearchResults([]);
                          }}
                          disabled={saving}
                          style={{
                            padding: '6px 10px',
                            border: 'none',
                            borderRadius: '4px',
                            backgroundColor: expandedPackageIndex === index ? '#4F46E5' : '#e5e7eb',
                            color: expandedPackageIndex === index ? '#fff' : '#374151',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            flexShrink: 0,
                          }}
                        >
                          <FontAwesomeIcon icon={faSearch} /> 상품
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemovePackage(index)}
                          disabled={saving}
                          aria-label="패키지 삭제"
                          title="패키지 삭제"
                          style={{
                            width: '38px',
                            height: '34px',
                            padding: 0,
                            border: '1px solid #fecaca',
                            borderRadius: '4px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            fontSize: '14px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>

                      {/* 선택된 상품 태그 (상품명으로 표시) */}
                      {(pkg.products?.length ?? 0) > 0 && (
                        <div style={{ padding: '0 12px 8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {pkg.products?.map(product => (
                            <span
                              key={product.id}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '2px 8px',
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                borderRadius: '4px',
                                fontSize: '12px',
                              }}
                            >
                              {product.productName}
                              <button
                                type="button"
                                onClick={() => removeProductFromPackage(index, product.id)}
                                style={{
                                  border: 'none',
                                  background: 'none',
                                  color: '#1e40af',
                                  cursor: 'pointer',
                                  padding: '0 2px',
                                  fontSize: '12px',
                                }}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* 상품 검색 영역 (펼침) */}
                      {expandedPackageIndex === index && (
                        <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
                          <div style={{ position: 'relative' }}>
                            <input
                              type="text"
                              value={editingPackageProductSearch}
                              onChange={e => handleProductSearchChange(e.target.value, false, getPackagePersonType(pkg, form.personType))}
                              placeholder="상품명으로 검색..."
                              disabled={saving}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px',
                              }}
                            />
                            {productSearchLoading && (
                              <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#6b7280' }}>
                                검색 중...
                              </span>
                            )}
                          </div>
                          {productSearchResults.length > 0 && (
                            <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px' }}>
                              {productSearchResults.map(product => {
                                const isSelected = pkg.products?.some(p => p.id === product.id);
                                return (
                                  <div
                                    key={product.id}
                                    onClick={() => !isSelected && addProductToPackage(index, product)}
                                    style={{
                                      padding: '8px 12px',
                                      borderBottom: '1px solid #f3f4f6',
                                      cursor: isSelected ? 'default' : 'pointer',
                                      backgroundColor: isSelected ? '#f0fdf4' : '#fff',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <span style={{ fontSize: '13px' }}>{product.productName}</span>
                                    <span style={{ fontSize: '12px', color: isSelected ? '#16a34a' : '#6b7280' }}>
                                      {isSelected ? '선택됨' : `${product.discountPrice.toLocaleString()}원`}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {editingPackageProductSearch && productSearchResults.length === 0 && !productSearchLoading && (
                            <p style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>검색 결과가 없습니다.</p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* 새 패키지 추가 */}
              <div
                style={{
                  border: '2px dashed #d1d5db',
                  borderRadius: '8px',
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
              >
                {/* 패키지명, 가격 */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 12px',
                  }}
                >
                  <input
                    type="text"
                    value={newPackageName}
                    onChange={e => setNewPackageName(e.target.value)}
                    placeholder="패키지명"
                    disabled={saving}
                    style={{
                      flex: 2,
                      padding: '6px 10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <input
                    type="number"
                    value={newPackagePrice}
                    onChange={e => setNewPackagePrice(e.target.value)}
                    placeholder="가격"
                    disabled={saving}
                    onWheel={preventWheelChange}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                    }}
                  />
                  <span style={{ fontSize: '14px', color: '#6b7280' }}>원</span>
                  <select
                    value={newPackagePersonType}
                    onChange={e => {
                      setNewPackagePersonType(e.target.value === '2' ? 2 : 1);
                      setSelectedProductsForNewPackage([]);
                      setProductSearchQuery('');
                      setProductSearchResults([]);
                    }}
                    disabled={saving}
                    style={{
                      width: '76px',
                      padding: '6px 8px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px',
                      backgroundColor: '#fff',
                    }}
                  >
                    <option value={1}>1인</option>
                    <option value={2}>2인</option>
                  </select>
                </div>

                {/* 상품 검색 영역 */}
                <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb', backgroundColor: '#fafafa' }}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={productSearchQuery}
                      onChange={e => handleProductSearchChange(e.target.value, true, newPackagePersonType)}
                      placeholder="포함할 상품 검색..."
                      disabled={saving}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                      }}
                    />
                    {productSearchLoading && (
                      <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#6b7280' }}>
                        검색 중...
                      </span>
                    )}
                  </div>
                  {productSearchQuery && productSearchResults.length > 0 && (
                    <div style={{ marginTop: '8px', maxHeight: '200px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: '#fff' }}>
                      {productSearchResults.map(product => {
                        const isSelected = selectedProductsForNewPackage.some(p => p.id === product.id);
                        return (
                          <div
                            key={product.id}
                            onClick={() => toggleProductForNewPackage(product)}
                            style={{
                              padding: '8px 12px',
                              borderBottom: '1px solid #f3f4f6',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? '#f0fdf4' : '#fff',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <span style={{ fontSize: '13px' }}>{product.productName}</span>
                            <span style={{ fontSize: '12px', color: isSelected ? '#16a34a' : '#6b7280' }}>
                              {isSelected ? '선택됨' : `${product.discountPrice.toLocaleString()}원`}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {productSearchQuery && productSearchResults.length === 0 && !productSearchLoading && (
                    <p style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280' }}>검색 결과가 없습니다.</p>
                  )}
                </div>

                {/* 선택된 상품 태그 */}
                {selectedProductsForNewPackage.length > 0 && (
                  <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '4px' }}>선택된 상품:</span>
                    {selectedProductsForNewPackage.map(product => (
                      <span
                        key={product.id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#dbeafe',
                          color: '#1e40af',
                          borderRadius: '4px',
                          fontSize: '12px',
                        }}
                      >
                        {product.productName}
                        <button
                          type="button"
                          onClick={() => toggleProductForNewPackage(product)}
                          style={{
                            border: 'none',
                            background: 'none',
                            color: '#1e40af',
                            cursor: 'pointer',
                            padding: '0 2px',
                            fontSize: '12px',
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* 추가 버튼 */}
                <div style={{ padding: '8px 12px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    type="button"
                    onClick={handleAddPackage}
                    disabled={saving}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: 'none',
                      borderRadius: '4px',
                      backgroundColor: '#4F46E5',
                      color: '#fff',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    패키지 추가
                  </button>
                </div>
              </div>
            </div>

            <hr className="form-section-divider" />

            <div className="form-field">
              <label style={{ fontSize: '16px', fontWeight: '600' }}>
                입금 계좌 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <p className="form-field-desc">알림톡에 포함될 입금 계좌 정보입니다.</p>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '80px', fontSize: '14px', fontWeight: '600' }}>
                    은행명
                  </span>
                  <Input
                    value={form.bankName}
                    onChange={handleInputChange('bankName')}
                    placeholder="예: 국민은행"
                    width="100%"
                    textAlign="left"
                    disabled={saving}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '80px', fontSize: '14px', fontWeight: '600' }}>
                    계좌번호
                  </span>
                  <Input
                    value={form.bankAccount}
                    onChange={handleInputChange('bankAccount')}
                    placeholder="예: 123-456-789012"
                    width="100%"
                    textAlign="left"
                    disabled={saving}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ minWidth: '80px', fontSize: '14px', fontWeight: '600' }}>
                    예금주
                  </span>
                  <Input
                    value={form.bankAccountHolder}
                    onChange={handleInputChange('bankAccountHolder')}
                    placeholder="예: 홍길동"
                    width="100%"
                    textAlign="left"
                    disabled={saving}
                  />
                </div>
              </div>
            </div>

            <div className="form-field">
              <label style={{ fontSize: '16px', fontWeight: '600' }}>구매 버튼명 (선택)</label>
              <p className="form-field-desc">랜딩페이지의 구매 신청 버튼에 표시될 텍스트입니다. 기본값: 구매 신청하기 (최대 20자)</p>
              <Input
                value={form.purchaseButtonName}
                onChange={handleInputChange('purchaseButtonName')}
                placeholder="구매 신청하기"
                width="100%"
                textAlign="left"
                disabled={saving}
                maxLength={20}
              />
            </div>

            <div className="form-field">
              <label style={{ fontSize: '16px', fontWeight: '600' }}>탭 타이틀 (선택)</label>
              <p className="form-field-desc">브라우저 탭에 표시될 제목입니다. 기본값: 구매 신청 (최대 50자)</p>
              <Input
                value={form.documentTitle}
                onChange={handleInputChange('documentTitle')}
                placeholder="구매 신청"
                width="100%"
                textAlign="left"
                disabled={saving}
                maxLength={50}
              />
            </div>

            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>푸터 표시</span>
                  <p className="form-field-desc" style={{ marginTop: '4px', marginBottom: 0 }}>
                    랜딩페이지 하단의 사업자 정보 푸터를 노출합니다.
                  </p>
                </div>
                <div
                  style={{
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    backgroundColor: form.showFooter ? '#4F46E5' : '#D1D5DB',
                    borderRadius: '12px',
                    transition: 'background-color 0.2s',
                    opacity: saving ? 0.5 : 1,
                    flexShrink: 0,
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                  onClick={e => {
                    e.preventDefault();
                    if (!saving) {
                      setForm(prev => ({ ...prev, showFooter: !prev.showFooter }));
                    }
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: form.showFooter ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>
              </label>
            </div>

            <hr className="form-section-divider" />

            <div className="form-field">
              <label style={{ fontSize: '16px', fontWeight: '600' }}>상단 이미지 (선택)</label>
              <p className="form-field-desc">입력폼 위에 표시되는 이미지입니다.</p>
              <FileUpload
                setFiles={setPendingTopFiles}
                accept="image/*"
                multiple
                note="이미지 또는 GIF, 최대 20MB"
              />
              {hasTopImages && renderAssetList(form.topImageUrls, 'top')}
            </div>

            <div className="form-field">
              <label style={{ fontSize: '16px', fontWeight: '600' }}>하단 이미지 (선택)</label>
              <p className="form-field-desc">입력폼 아래에 표시되는 이미지입니다.</p>
              <FileUpload
                setFiles={setPendingBottomFiles}
                accept="image/*"
                multiple
                note="이미지 또는 GIF, 최대 20MB"
              />
              {hasBottomImages && renderAssetList(form.bottomImageUrls, 'bottom')}
            </div>

            <div className="form-field">
              <label
                style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
              >
                <span>픽셀 사용(선택)</span>
                <div
                  style={{
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    backgroundColor: showPixelSettings ? '#4F46E5' : '#D1D5DB',
                    borderRadius: '12px',
                    transition: 'background-color 0.2s',
                    opacity: saving ? 0.5 : 1,
                  }}
                  onClick={e => {
                    e.preventDefault();
                    if (!saving) setShowPixelSettings(!showPixelSettings);
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: showPixelSettings ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>
              </label>

              {showPixelSettings && (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600' }}>
                      Facebook
                    </span>
                    <Input
                      value={form.facebookPixelId}
                      onChange={handleInputChange('facebookPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600' }}>
                      카카오
                    </span>
                    <Input
                      value={form.kakaoPixelId}
                      onChange={handleInputChange('kakaoPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600' }}>
                      Google Ads
                    </span>
                    <Input
                      value={form.googleAdsPixelId}
                      onChange={handleInputChange('googleAdsPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                    <Input
                      value={form.googleAdsEventSnippet}
                      onChange={handleInputChange('googleAdsEventSnippet')}
                      placeholder="이벤트 스니펫"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600' }}>
                      당근
                    </span>
                    <Input
                      value={form.daangnPixelId}
                      onChange={handleInputChange('daangnPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600' }}>
                      TikTok
                    </span>
                    <Input
                      value={form.tiktokPixelId}
                      onChange={handleInputChange('tiktokPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="preview-column">
            <section className="preview-panel">
              <h3>랜딩 미리보기</h3>
              <div className="landing-preview">
                <div className="landing-preview__content">
                  {/* 상단 이미지 */}
                  <div className="landing-preview__media">
                    {hasTopImages ? (
                      form.topImageUrls.map((asset, index) => (
                        <img
                          key={`top-${asset}-${index}`}
                          src={asset}
                          alt={`상단 이미지 ${index + 1}`}
                        />
                      ))
                    ) : (
                      <div className="landing-preview__placeholder">
                        상단 이미지를 업로드하면 이곳에 표시됩니다.
                      </div>
                    )}
                  </div>

                  {/* 입력폼 미리보기 */}
                  {previewPersonType === 1 ? render1PersonPreview() : render2PersonPreview()}

                  <div className="privacy_agreement">
                    <label className="privacy_agreement_checkbox">
                      <input type="checkbox" disabled />
                      <span className="privacy_agreement_text">개인정보 수집·이용에 동의합니다</span>
                    </label>
                  </div>

                  <div className="privacy_agreement">
                    <label className="privacy_agreement_checkbox">
                      <input type="checkbox" disabled />
                      <span className="privacy_agreement_text">개인정보 제3자 제공에 동의합니다</span>
                    </label>
                  </div>

                  <button
                    type="button"
                    disabled
                    style={{
                      padding: '14px 20px',
                      backgroundColor: '#FFD700',
                      color: '#000',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'not-allowed',
                      marginTop: '12px',
                    }}
                  >
                    {form.purchaseButtonName.trim() || '구매 신청하기'}
                  </button>

                  {/* 하단 이미지 */}
                  {hasBottomImages && (
                    <div className="landing-preview__media landing-preview__media--bottom">
                      {form.bottomImageUrls.map((asset, index) => (
                        <img
                          key={`bottom-${asset}-${index}`}
                          src={asset}
                          alt={`하단 이미지 ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  {form.showFooter && (
                    <LandingBusinessFooter
                      companyNumber={myfranchise?.companyNumber}
                      mailOrderNumber={getFranchiseMailOrderNumber(myfranchise)}
                      companyName={myfranchise?.companyName}
                      ceo={myfranchise?.ceo}
                      serviceName={myfranchise?.bizProfileName}
                    />
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>

        <div className="fortunelab_modal_footer">
          <BigButton
            type="submit"
            text={submitLabel}
            onClick={handleSubmit}
            style={{ flex: '0 0 50%' }}
            disabled={saving || uploading}
          />
        </div>
      </div>
    </div>
  );
};

export default PurchaseLandingTrackingCodeModal;
